import {
  type DocumentData,
  type Firestore,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { WarrantySchema, WarrantyCreateInputSchema, WarrantyUpdateInputSchema, type Warranty, type WarrantyCreateInput, type WarrantyUpdateInput } from '@taki/data-model';
import { CURRENT_SCHEMA_VERSION, buildUpdateData, createConverter } from './standard-fields.js';
import { type CountOptions, type ListOptions, runCount, runList } from './list-query.js';
import { createArchiveOps } from './archive-ops.js';

/**
 * Repository de la famille `warranty` (couverture dans le temps, org-wide).
 *
 * Calque le patron `asset` : l'entrée est VALIDÉE par Zod
 * (`WarrantyCreateInputSchema`) AVANT toute écriture Firestore. Les champs
 * standard sont remplis par le SDK, les requêtes `list` sont cadrées par orgId.
 */

export type { WarrantyCreateInput };

/** Nom de la collection Firestore de la famille warranty. */
export const WARRANTIES_COLLECTION = 'warranties';

/**
 * Repository typé pour la famille `warranty`.
 * Toute lecture est validée par `WarrantySchema`, toute écriture par
 * `WarrantyCreateInputSchema` (cage IA : aucun accès Firestore direct hors SDK).
 */
export interface WarrantyRepository {
  create(input: WarrantyCreateInput): Promise<Warranty>;
  update(id: string, patch: WarrantyUpdateInput): Promise<Warranty>;
  list(options?: ListOptions): Promise<Warranty[]>;
  count(options?: CountOptions): Promise<number>;
  get(id: string): Promise<Warranty | null>;
  /** Archive (`archivedAt`). Semantique commune : cf. archive-ops.ts. */
  archive(id: string): Promise<Warranty>;
  /** Desarchive (`archivedAt` = null, JAMAIS deleteField). Cf. archive-ops.ts. */
  unarchive(id: string): Promise<Warranty>;
  /** Supprime (`deletedAt` + `archivedAt`), invisible cote client. Cf. archive-ops.ts. */
  remove(id: string): Promise<Warranty>;
}

const warrantyConverter = createConverter<Warranty>((raw: DocumentData) => WarrantySchema.parse(raw));

/**
 * @param getActorId - retourne l'uid de l'utilisateur authentifié courant
 *   (injecté par `createTakiSdk`). Les Security Rules exigent
 *   createdBy/updatedBy == request.auth.uid.
 */
export function createWarrantyRepository(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
): WarrantyRepository {
  const col = collection(db, WARRANTIES_COLLECTION).withConverter(warrantyConverter);
  // Archiver, desarchiver, supprimer : la semantique des trois etats vit dans
  // `createArchiveOps` (archive-ops.ts), ecrite une seule fois pour les 55
  // familles. Une famille n'a plus rien a decider ici.
  const archiveOps = createArchiveOps<Warranty>(db, col, WARRANTIES_COLLECTION, getActorId);

  return {
    async create(input: WarrantyCreateInput): Promise<Warranty> {
      // Valider AVANT d'écrire : une entrée invalide échoue ici, rien n'atteint
      // Firestore.
      const fields = WarrantyCreateInputSchema.parse(input);

      const actor = getActorId();
      const ref = doc(col);
      const now = new Date();

      // Champs métier optionnels : on ne les écrit que s'ils sont fournis
      // (évite d'ancrer des `undefined` / `null` parasites dans Firestore).
      const optional: DocumentData = {};
      if (fields.assetId !== undefined) optional['assetId'] = fields.assetId;
      if (fields.orderLineRef !== undefined) optional['orderLineRef'] = fields.orderLineRef;
      if (fields.workOrderId !== undefined) optional['workOrderId'] = fields.workOrderId;
      if (fields.startAt !== undefined) optional['startAt'] = fields.startAt;
      if (fields.durationMonths !== undefined) optional['durationMonths'] = fields.durationMonths;
      if (fields.terms !== undefined) optional['terms'] = fields.terms;
      if (fields.claimTicketIds !== undefined) optional['claimTicketIds'] = fields.claimTicketIds;
      if (fields.customFields !== undefined) optional['customFields'] = fields.customFields;

      const payload: DocumentData = {
        orgId: fields.orgId,
        type: fields.type,
        ...optional,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: actor,
        updatedBy: actor,
        archivedAt: null,
        deletedAt: null,
        schemaVersion: CURRENT_SCHEMA_VERSION,
      };

      await setDoc(ref, payload as unknown as Warranty);

      // setDoc ne résout pas serverTimestamp() : on valide une projection
      // cohérente avec ce qui vient d'être écrit.
      return WarrantySchema.parse({
        id: ref.id,
        ...payload,
        createdAt: now,
        updatedAt: now,
      });
    },

    async update(id: string, patch: WarrantyUpdateInput): Promise<Warranty> {
      // Valider AVANT d'ecrire (meme doctrine que create).
      const fields = WarrantyUpdateInputSchema.parse(patch);
      const actor = getActorId();

      // Patch transforme pour updateDoc : customFields est eclate en chemins
      // pointes ('customFields.<cle>') pour fusionner cle par cle au lieu de
      // remplacer la map entiere (cf. buildUpdateData ; null = deleteField).
      const data = buildUpdateData(fields);
      data['updatedAt'] = serverTimestamp();
      data['updatedBy'] = actor;

      // Ref brute (sans converter) pour l'ecriture partielle : le converter
      // exigerait le document complet.
      await updateDoc(doc(db, WARRANTIES_COLLECTION, id), data);

      // Relire le document reel via la ref convertie : source de verite.
      const snap = await getDoc(doc(col, id));
      if (!snap.exists()) {
        throw new Error(`warranty ${id} introuvable apres mise a jour.`);
      }
      return snap.data();
    },

    async list(options?: ListOptions): Promise<Warranty[]> {
      // Cadrage orgId, exclusion des archives, sous-type, tri, taille de page
      // et curseur : toute la semantique vit dans `runList` (list-query.ts),
      // ecrite une seule fois pour les 55 familles.
      return runList(col, orgId, options);
    },

    async count(options?: CountOptions): Promise<number> {
      // Compter sans charger : meme cadrage que `list`, sans page ni tri
      // (cf. runCount, list-query.ts). Un compteur calcule sur la page
      // courante ment des que la liste est paginee.
      return runCount(col, orgId, options);
    },

    async get(id: string): Promise<Warranty | null> {
      const ref = doc(col, id);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return snap.data();
    },

    // archive / unarchive / remove : cf. `archiveOps` plus haut.
    ...archiveOps,
  };
}
